import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Award, ShieldCheck, Sparkles, Wrench } from "lucide-react";
import api from "../lib/api";
import { FadeUp, Counter } from "../components/Motion";
import { LocalBusinessSchema } from "../components/SeoSchema";
import GoogleReviews from "../components/GoogleReviews";
import PageMeta from "../components/PageMeta";

const VALUES = [
  {
    icon: Sparkles,
    title: "Craftsmanship",
    body: "Architectural rigour at every scale — from base systems to final lighting layer.",
  },
  {
    icon: ShieldCheck,
    title: "Trust",
    body: "Fully licensed and insured. Transparent budgets, fixed proposals, no surprises.",
  },
  {
    icon: Wrench,
    title: "Speed",
    body: "Engineered processes that move from concept to install without sacrificing finish.",
  },
  {
    icon: Award,
    title: "Detail",
    body: "Every joint, edge, and grade obsessed over. The detail is the project.",
  },
];

function FounderInitial({ name }) {
  const letter = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="w-full h-full bg-gradient-to-br from-[#0B2B1F] to-[#151515] flex items-center justify-center">
      <span className="font-serif-display text-[10rem] md:text-[12rem] leading-none text-[#F5F3EF]/15 select-none">
        {letter}
      </span>
    </div>
  );
}

export default function About() {
  const [founders, setFounders] = useState([]);

  useEffect(() => {
    api.get("/founders").then((r) => setFounders(r.data || [])).catch(() => {});
  }, []);

  const andrew = useMemo(() => founders.find((f) => f.slug === "andrew"), [founders]);
  const slava = useMemo(() => founders.find((f) => f.slug === "slava"), [founders]);

  return (
    <div data-testid="about-page" className="pt-32 pb-32">
      <PageMeta
        title="About — Andrew & Slava · NorthCrest Landscaping Edmonton"
        description="Meet Andrew and Slava — the owners of NorthCrest Landscaping. Premium landscape construction and outdoor living across Edmonton, Alberta."
        path="/about"
      />
      <LocalBusinessSchema />

      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-16 mb-20 md:mb-28">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">About · Owners</div>
          <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
            Hi, we are <span className="italic text-[#D8D6D0]">Andrew &amp; Slava.</span>
          </h1>
          <p className="text-[#D8D6D0]/70 mt-8 max-w-2xl text-lg md:text-xl font-light leading-relaxed">
            The owners of NorthCrest Landscaping. Two builders, one studio — and one obsession with making outdoor spaces that hold up to Alberta weather and Alberta standards.
          </p>
        </FadeUp>
      </section>

      {/* FOUNDERS — portraits + bios */}
      <section className="px-6 md:px-12 lg:px-16 mb-24 md:mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {[andrew, slava].filter(Boolean).map((f, i) => (
            <FadeUp key={f.slug} delay={i * 0.1}>
              <article data-testid={`founder-${f.slug}`} className="group">
                <div className="aspect-[4/5] overflow-hidden border border-[#D8D6D0]/10 bg-[#0d0d0d]">
                  {f.image_url ? (
                    <img
                      src={f.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${f.image_url}` : f.image_url}
                      alt={f.name}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.02]"
                    />
                  ) : (
                    <FounderInitial name={f.name} />
                  )}
                </div>
                <div className="mt-6">
                  <div className="overline text-[#8C1D30] mb-2">Co-Founder</div>
                  <h3 className="font-serif-display text-4xl md:text-5xl">{f.name}</h3>
                  <div className="overline text-[#D8D6D0]/50 mt-2">{f.role}</div>
                  <p className="mt-5 text-[#D8D6D0]/75 leading-relaxed max-w-prose">{f.bio}</p>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* STORY — adapted from user copy */}
      <section className="px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 mb-32">
        <FadeUp className="md:col-span-7">
          <div className="overline text-[#8C1D30] mb-4">Our Story</div>
          <p className="font-serif-display text-2xl md:text-3xl leading-snug">
            If you are looking for a reliable landscape construction company that delivers truly premium outdoor living, you&apos;ve come to the right place.
          </p>
          <p className="text-[#D8D6D0]/70 mt-6 text-lg leading-relaxed">
            Andrew and Slava, the owners of NorthCrest Landscaping, are committed to making sure every project results in outstanding craftsmanship and exceptional service. With their direct involvement on every job, you can trust that our specialists will deliver remarkable results — every time.
          </p>
          <p className="text-[#D8D6D0]/70 mt-6 text-lg leading-relaxed">
            At NorthCrest, we operate as a team of first-class craftsmen with over 6 years of experience in residential landscaping, commercial grounds, and custom outdoor living. No site is too complex. Our team is built to tackle Alberta&apos;s hardest conditions — bringing improvements and ensuring each project is completed quickly, efficiently, and to the highest standards.
          </p>
          <p className="text-[#D8D6D0]/70 mt-6 text-lg leading-relaxed">
            Our services guarantee quality and satisfaction, no matter the size or difficulty of the build.
          </p>
        </FadeUp>
        <FadeUp delay={0.15} className="md:col-span-5">
          <div className="aspect-[3/4] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1761637823407-ef47925c2714?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="NorthCrest studio"
              className="w-full h-full object-cover"
            />
          </div>
        </FadeUp>
      </section>

      {/* VALUES */}
      <section className="px-6 md:px-12 lg:px-16 py-24 border-y border-[#D8D6D0]/10">
        <FadeUp>
          <div className="overline text-[#8C1D30] mb-4">What we stand for</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mb-16 max-w-3xl">
            Four values, on every project.
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {VALUES.map((v, i) => (
            <FadeUp key={v.title} delay={i * 0.05}>
              <div data-testid={`value-${i}`} className="border-l border-[#D8D6D0]/15 pl-6 py-2">
                <v.icon className="w-6 h-6 text-[#8C1D30] mb-5" />
                <div className="font-serif-display text-2xl mb-3">{v.title}</div>
                <p className="text-sm text-[#D8D6D0]/65 leading-relaxed">{v.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="px-6 md:px-12 lg:px-16 py-24 grid grid-cols-2 md:grid-cols-4 gap-12 border-b border-[#D8D6D0]/10">
        {[
          { n: 2020, suffix: "", label: "Founded" },
          { n: 6, suffix: "+", label: "Years Building" },
          { n: 200, suffix: "+", label: "Projects" },
          { n: 7, suffix: "", label: "Service Areas" },
        ].map((s, i) => (
          <FadeUp key={s.label} delay={i * 0.05}>
            <div className="font-serif-display text-5xl md:text-6xl">
              <Counter value={s.n} suffix={s.suffix} />
            </div>
            <div className="overline text-[#D8D6D0]/50 mt-3">{s.label}</div>
          </FadeUp>
        ))}
      </section>

      {/* TEAM section removed per request */}

      {/* GOOGLE REVIEWS */}
      <GoogleReviews />

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-16 py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
          <FadeUp>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-tight max-w-xl">
              Start your <span className="italic text-[#D8D6D0]">project</span> with us.
            </h2>
            <p className="text-[#D8D6D0]/65 mt-6 max-w-md text-lg">
              A senior designer — usually Andrew or Slava — will respond within one business day.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
              <Link
                to="/estimate"
                data-testid="about-cta-estimate"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#8C1D30] hover:bg-[#7A1829] text-[#F5F3EF] text-sm tracking-wider uppercase transition-colors duration-300"
              >
                Get Free Estimate <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/projects"
                data-testid="about-cta-projects"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#F5F3EF]/40 hover:bg-[#F5F3EF] hover:text-[#151515] text-[#F5F3EF] text-sm tracking-wider uppercase transition-all duration-300"
              >
                View Projects
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
